/* eslint-disable camelcase */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Check, Close, EditCalendarOutlined, MarkEmailReadOutlined, PriorityHigh, Send } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAllSendingOrderProduct,
  getSendingDateById,
  getSendingOrderById,
  deleteSendingOrder,
  orderSendingOrderReminder,
  updateSendingOrder,
} from 'src/shared/services/sendingService';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { getAllOrganizations } from 'src/shared/services/organizationService';
import { createInvoiceTemplate } from 'src/shared/services/invoiceService';
import { deriveMembershipYear } from 'src/modules/sending/functions/membershipYear';
import { formatCustomerSendDate } from 'src/modules/sending/functions/customerSendDate';
import { computeSegments } from 'src/modules/sending/functions/deliveryReach';
import { SendingOrderDateModal } from 'src/modules/sending/components/sendingOrderDateModal';
import { SendingOrderStatusBadge } from 'src/modules/sending/components/sendingOrderStatusBadge';
import { SendingOrderReminderLanding } from 'src/modules/sending/components/sendingOrderReminderLanding';
import { SendingWizardStepReminderDate } from 'src/modules/sending/components/sendingWizardStepReminderDate';
import { SendingOrder } from 'src/modules/sending/models/sendingOrder';
import { SendingOrderProduct } from 'src/modules/sending/models/sendingOrderProduct';
import { brevArticlePrefix } from 'src/modules/sending/functions/sendingPricing';
import { InvoiceTemplate, Sending } from 'src/modules/invoice/models/invoiceTemplate';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { SendingOrderStatusEnum } from 'src/modules/sending/models/sendingOrderStatus';
import { FormSelectableStateProvider } from 'src/shared/components/form/formSelectableStateContext';
import { useMultiStepForm } from 'src/shared/hooks/useMultiStepForm';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { SendingWizardStep2Type } from 'src/modules/sending/components/sendingWizardStep2Type';
import { SendingWizardStep4Delivery } from 'src/modules/sending/components/sendingWizardStep4Delivery';
import { SendingWizardStep5Selection } from 'src/modules/sending/components/sendingWizardStep5Selection';
import { SendingWizardStep6Texts } from 'src/modules/sending/components/sendingWizardStep6Texts';
import { SendingWizardStep7Summary } from 'src/modules/sending/components/sendingWizardStep7Summary';
import { SendingWizardProvider, useSendingWizard, ProfileFilter, SelectionCoverage } from 'src/modules/sending/contexts/sendingWizardContext';

// Fas B: kunden fortsätter på en redan sparad (Pågående) beställning. Här väljs typ av utskick,
// medlemsurval, leveransmetoder och texter. "Skicka in beställning" sätter status Inskickad och mejlar FS.
const stepLabels = ['Typ av utskick', 'Medlemmar', 'Leveransmetod/er', 'Texter', 'Sammanfattning'];
// Utan typval (bara en utskickstyp finns) börjar guiden direkt med medlemmarna.
const stepLabelsWithoutType = ['Medlemmar', 'Leveransmetod/er', 'Texter', 'Sammanfattning'];
// När huvudutskicket är inskickat väljer steg 1 utskicksled i stället för typ, och en påminnelse har mottagare
// i stället för ett eget medlemsurval.
const ledStepLabels = ['Datum', 'Leveransmetod/er', 'Texter', 'Sammanfattning'];

const isEmpty = (v: unknown) => v === '' || v === null || v === undefined;

// Produktionsdeadline: beställningen måste vara komplett så här många dygn före utskicksdatumet (DB-datum).
// Samma gräns som påminnelse-jobbet i TaskService använder.
const PRODUCTION_DEADLINE_DAYS = 7;

// Bygger sträng-representationen av profilfilter som fakturamallen förväntar sig: par av "grupp,post".
const profilePairs = (filters: { groupId: number; itemId: number }[]) => filters.flatMap((f) => [f.groupId, f.itemId]).join(',');

// Guidens arbetskopia som serialiseras till snd_order.wizardData (JSON) vid varje stegbyte.
interface WizardDataDraft {
  selectionTab?: number;
  dueDateFrom?: string | null;
  dueDateTom?: string | null;
  profileFilters?: ProfileFilter[];
  excludeProfileFilters?: ProfileFilter[];
  dueConfirmed?: boolean;
  includeConfirmed?: boolean;
  excludeConfirmed?: boolean;
  emailHeading?: string;
  emailText?: string;
  invoiceHeading?: string;
  invoiceText?: string;
  emailTextConfirmed?: boolean;
  invoiceTextConfirmed?: boolean;
  blankettConfirmed?: boolean;
  // "Jag kompletterar senare" – sparas så läget överlever återöppning även för utkast (annars härleds det ur status).
  completeLater?: boolean;
  // Samlingsfaktura (default true) – hushållsmedlemmar slås ihop på en faktura till huvudmedlemmen.
  householdInvoice?: boolean;
  // Skapa faktura även för avgiftsfria medlemmar (default false) – makulerad 0-kr-faktura för t.ex. hedersmedlemmar.
  printZeroInvoice?: boolean;
  // Logotyp på fakturan: false = förbundets (centrala) logga, true = föreningens egna logga.
  useLocalAssLogo?: boolean;
  // Vald fakturablankett (bgocr/pgocr/blank) – begränsas av faktureringsgruppens kontotyp.
  formTemplate?: string;
  // Steg 4-lägesknappar: texterna hämtade från tidigare avisering resp. "Jag skriver texter" aktivt valt.
  // Sparas så att INGEN knapp är förvald förrän kunden valt (och rätt knapp återställs vid återöppning).
  textsFromPrevious?: boolean;
  textsWriteChosen?: boolean;
  // Färgval för brevet (svartvitt/färg) från sammanfattningssteget – styr brev-artikeln (och priset).
  letterColor?: string;
  // Kanal-täckningen (antal nåbara per kanal) räknas fram i Medlemmar-steget och sparas så leveranssteget kan
  // visa siffrorna direkt när en beställning återupptas – utan att steg 2 måste besökas om.
  coverage?: SelectionCoverage | null;
  // Påbörjade men ej inskickade påminnelser, nycklade på led ("1"/"2"). Beställda påminnelser lever i egna
  // kolumner på beställningen – det här är bara arbetskopian fram till inskickning.
  reminders?: Record<string, ReminderDraft>;
}

// En påminnelses arbetskopia. Samma fält som huvudutskicket använder i formuläret – ledet avgör vart de sparas.
interface ReminderDraft {
  date_id?: number;
  payDate?: string;
  deliveryMethodOrder?: string;
  emailHeading?: string;
  emailText?: string;
  invoiceHeading?: string;
  invoiceText?: string;
  // Klarmarkeringarna i Texter-steget hör till det enskilda utskicket – annars ser Påminnelse 2 grön ut bara för
  // att huvudutskickets texter är klara.
  emailTextConfirmed?: boolean;
  invoiceTextConfirmed?: boolean;
  // Lägesknapparna i Texter-steget (hämta från tidigare avisering / skriv själv). Måste också vara per utskick,
  // annars hoppar påminnelsen direkt in i editorerna med huvudutskickets val.
  textsFromPrevious?: boolean;
  textsWriteChosen?: boolean;
}

// Själva guiden ligger inne i SendingWizardProvider så att både urvalet (context) och texterna (form)
// kan sparas ner i beställningen vid varje stegbyte och läsas tillbaka när beställningen öppnas igen.
const SendingOrderWizardBody = () => {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { id } = useParams();
  const { currentUser } = useGlobals();
  const [sendingOrder, setSendingOrder] = useState<SendingOrder | null>(null);
  const [saving, setSaving] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  // Efter inskickad beställning: kvittodialog som visar att en bekräftelse mejlats till beställaren (navigerar vid stängning).
  const [submittedInfo, setSubmittedInfo] = useState<{ email: string; status: number } | null>(null);
  // Marker högst upp – används för att scrolla upp till progressen vid stegbyte.
  // Scrollbara innehållsytan i fullskärms-modalen (scrollas till toppen vid stegbyte).
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollToTop = useCallback(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  // Spinner mellan stegen tills nästa fadar in (samma mjuka övergång som skapa-modalen).
  const [transitioning, setTransitioning] = useState(false);
  // Produkterna behövs för att härleda utskickstyp (medlem/påminnelse) ur produktnamnet vid inskick.
  const [products, setProducts] = useState<SendingOrderProduct[]>([]);
  // Påbörjade påminnelser (arbetskopia). Beställda påminnelser läses i stället från beställningens kolumner.
  const [reminderDrafts, setReminderDrafts] = useState<Record<string, ReminderDraft>>({});
  // Huvudutskickets steg öppnade från landningsvyn (för att komplettera eller läsa igenom beställningen).
  const [mainStepsOpen, setMainStepsOpen] = useState(false);
  // Avboka-dialogen (raderar beställningen).
  const [cancelOpen, setCancelOpen] = useState(false);
  // Är Kivra aktiverat för faktureringsgruppen? null = inte kontrollerat ännu.
  const [kivraActivated, setKivraActivated] = useState<boolean | null>(null);
  // Vilket utskicksled vi redan hoppat till "det som saknas" för (så hoppet sker en gång per led).
  const jumpedToPendingRef = useRef<number | null>(null);

  const { watch, getValues, reset, setValue } = useFormContext<SendingOrder>();
  const {
    selectionTab,
    setSelectionTab,
    dueDateFrom,
    setDueDateFrom,
    dueDateTom,
    setDueDateTom,
    profileFilters,
    setProfileFilters,
    excludeProfileFilters,
    setExcludeProfileFilters,
    dueConfirmed,
    setDueConfirmed,
    includeConfirmed,
    setIncludeConfirmed,
    excludeConfirmed,
    setExcludeConfirmed,
    emailTextConfirmed,
    setEmailTextConfirmed,
    invoiceTextConfirmed,
    setInvoiceTextConfirmed,
    blankettConfirmed,
    setBlankettConfirmed,
    coverage,
    setCoverage,
    membershipMMdd,
    setMembershipMMdd,
    gracePeriodDays,
    setGracePeriodDays,
    sendDate,
    setSendDate,
    textStepNav,
    led,
    setLed,
    setReminderCountsVisible,
    setReminderPreviousPayDate,
  } = useSendingWizard();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getSendingOrderById(id);
        if (!isMounted()) return;

        const { data } = result;

        // Läs tillbaka guidens arbetskopia (urval + texter) så kunden kan fortsätta där hen slutade.
        let w: WizardDataDraft = {};
        if (data.wizardData) {
          try {
            w = JSON.parse(data.wizardData) as WizardDataDraft;
          } catch (e) {
            ConsoleHelper.log('error parse wizardData', e);
          }
        }

        reset({
          ...data,
          emailHeading: w.emailHeading ?? '',
          emailText: w.emailText ?? '',
          invoiceHeading: w.invoiceHeading ?? '',
          invoiceText: w.invoiceText ?? '',
          letterColor: w.letterColor === 'color' ? 'color' : 'bw',
          completeLater: w.completeLater ?? data.status === SendingOrderStatusEnum.KompletteringBehovs,
          householdInvoice: w.householdInvoice ?? true,
          printZeroInvoice: w.printZeroInvoice ?? false,
          useLocalAssLogo: w.useLocalAssLogo ?? false,
          formTemplate: w.formTemplate ?? '',
          textsFromPrevious: w.textsFromPrevious ?? false,
          textsWriteChosen: w.textsWriteChosen ?? false,
        });
        setSendingOrder(data);
        setReminderDrafts(w.reminders ?? {});
        // En beställning som väntar på komplettering öppnas direkt i stegen – det är det som ska göras.
        // Landningsvyn nås ändå via "Till beställningen".
        if (data.status === SendingOrderStatusEnum.KompletteringBehovs) setMainStepsOpen(true);
        setSendDate(data.date?.sndDate ? dayjs(data.date.sndDate) : null);
        // Fallback: om beställnings-GET:en inte populerar date-objektet, hämta utskicksdatumet separat via date_id.
        if (!data.date?.sndDate && data.date_id) {
          try {
            const dres = await getSendingDateById(data.date_id);
            if (isMounted() && dres.data?.sndDate) setSendDate(dayjs(dres.data.sndDate));
          } catch (e) {
            ConsoleHelper.log('error getSendingDateById', e);
          }
        }

        // Hydrera urvalet i wizard-contexten (överlever inte reset – ligger utanför formuläret).
        // -1 = inget läge valt (ingen förvald knapp). Sparade utkast med ett valt läge hydreras till 0/1/2.
        setSelectionTab(w.selectionTab ?? -1);
        setDueDateFrom(w.dueDateFrom ? dayjs(w.dueDateFrom) : null);
        setDueDateTom(w.dueDateTom ? dayjs(w.dueDateTom) : null);
        setProfileFilters(Array.isArray(w.profileFilters) ? w.profileFilters : []);
        setExcludeProfileFilters(Array.isArray(w.excludeProfileFilters) ? w.excludeProfileFilters : []);
        setDueConfirmed(!!w.dueConfirmed);
        setIncludeConfirmed(!!w.includeConfirmed);
        setExcludeConfirmed(!!w.excludeConfirmed);
        setEmailTextConfirmed(!!w.emailTextConfirmed);
        setInvoiceTextConfirmed(!!w.invoiceTextConfirmed);
        setBlankettConfirmed(!!w.blankettConfirmed);
        setCoverage(w.coverage ?? null);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error getSendingOrderById', e);
      }
    };
    if (id) fetchData();
    // Setterfunktionerna (form + context) är stabila – effekten ska bara köra om vid byte av beställnings-id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Produkterna hämtas en gång för att kunna härleda utskickstyp (medlem/påminnelse) ur produktnamnet vid inskick.
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const result = await getAllSendingOrderProduct();
        // Påminnelser beställs som led på en aviseringsbeställning, inte som egen utskickstyp.
        const selectable = (result.data ?? []).filter((product) => !(product.name ?? '').toLowerCase().includes('påminnel'));
        if (isMounted()) setProducts(selectable);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error getAllSendingOrderProduct', e);
      }
    };
    fetchProducts();
  }, [isMounted]);

  // Förbundets förfallodatum (MMdd) + friperiod (dagar) hämtas en gång och delas via contexten
  // (utskicksdatum-panelen + Medlemmar-steget härleder medlemsår ur datum + friperiod).
  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await getAllOrganizations();
        const org = res.data.results?.[0];
        if (isMounted() && org) {
          const raw = (org.expireDate ?? '').trim();
          setMembershipMMdd(/^\d{4}$/.test(raw) ? raw : null);
          setGracePeriodDays(Number(org.gracePeriod) || 0);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error getAllOrganizations', e);
      }
    };
    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  // Kräver ett riktigt valt produkt-id (> 0) – default-värdet 0 (ingen typ vald) räknas inte som klart av isEmpty.
  const stepTypeComplete = Number(watch('product_id')) > 0;
  // Medlemmar-steget är klart när ett läge valts OCH det är ifyllt: fritext (1) kräver en beskrivning; eget urval (0)
  // och kopierat (2) kräver att alla tre korten (förfallodatum + inkludera + exkludera) är klarmarkerade. Inget läge
  // valt (selectionTab === -1) → inte klart, man får inte gå vidare.
  let stepMembersComplete = false;
  if (selectionTab === 1) stepMembersComplete = !isEmpty(watch('selectionDescription'));
  else if (selectionTab === 0 || selectionTab === 2) stepMembersComplete = dueConfirmed && includeConfirmed && excludeConfirmed;
  // Leverans-steget är klart när kedjan är "finaliserad": alla nås, eller alla tre nivåer använts (även om några då
  // inte kan nås). Annars finns en obesvarad fallback-nivå kvar → man får inte gå vidare.
  const deliveryOrderArr = ((watch('deliveryMethodOrder') as string) ?? '').split(',').filter((k) => k.length > 0);
  const deliveryReached = computeSegments(deliveryOrderArr, coverage).reduce((sum, s) => sum + s.inc, 0);
  const deliveryNotReached = coverage ? Math.max(0, coverage.total - deliveryReached) : 0;
  // Huvudutskicket kräver en "finaliserad" kedja: alla nås, eller alla tre nivåer använda. För en påminnelse
  // räcker det med minst en metod – kunden ska inte tvingas fylla nivåer för ett litet urval.
  const stepDeliveryComplete =
    led !== 0
      ? deliveryOrderArr.length > 0
      : deliveryOrderArr.length === 3 || (coverage != null && deliveryOrderArr.length > 0 && deliveryNotReached === 0);
  const completeLater = !!watch('completeLater');
  // E-post/Kivra-sendingen finns bara om utskicket går via e-post eller Kivra.
  const needsEmailText = ((watch('deliveryMethodOrder') as string) ?? '').split(',').some((m) => m === 'mail' || m === 'kivra');
  // Texter-steget är klart när varje relevant sending är klar-markerad (grönt kort) eller kunden valt att komplettera senare.
  // Fakturablanketten hör till huvudutskicket – en påminnelse ärver mallens blankett och har ingen egen flik att
  // klarmarkera, så den ingår inte i påminnelsens klar-villkor.
  const stepTextsComplete = completeLater || ((!needsEmailText || emailTextConfirmed) && invoiceTextConfirmed && (led !== 0 || blankettConfirmed));
  // Sammanfattningen är klar när färgval gjorts om brev ingår (färgvalet har default svartvitt), annars alltid klar.
  const hasLetter = ((watch('deliveryMethodOrder') as string) ?? '').split(',').includes('letter');
  const letterColorValue = (watch('letterColor') as string) || 'bw';
  const stepSummaryComplete = !hasLetter || letterColorValue === 'bw' || letterColorValue === 'color';
  // Sista dag att skicka in det AKTIVA utskicket: produktionsdeadlinen är 7 dygn före utskicksdatumet (DB-datum).
  // Kunden ser deadlinen en dag tidigare, samma −1-konvention som utskicksdatumet.
  const productionDeadline = sendDate ? sendDate.startOf('day').subtract(PRODUCTION_DEADLINE_DAYS, 'day') : null;
  const deadlinePassed = !!productionDeadline && dayjs().startOf('day').isAfter(productionDeadline, 'day');
  const deadlineDisplay = productionDeadline ? productionDeadline.subtract(1, 'day').format('D MMM YYYY') : '';

  // Kivra valt men inte aktiverat för faktureringsgruppen – då är det leveranssteget som behöver åtgärdas.
  const kivraChosen = ((watch('deliveryMethodOrder') as string) ?? '').split(',').some((m) => m.trim() === 'kivra');
  const kivraMissing = kivraChosen && kivraActivated === false;

  // Finns bara en utskickstyp behöver kunden inte välja – då hoppas typ-steget över helt.
  const onlyProduct = products.length === 1 ? products[0] : null;

  useEffect(() => {
    if (!onlyProduct) return;
    if (Number(getValues('product_id')) > 0) return;
    setValue('product_id', onlyProduct.id, { shouldValidate: true });
  }, [onlyProduct, getValues, setValue]);

  // Påminnelsens första steg är klart när både utskicksdatum och förfallodatum är valda.
  const stepReminderDateComplete = Number(watch('date_id')) > 0 && !isEmpty(watch('payDate'));
  // Typ-steget visas bara när det finns något att välja mellan (fler än en utskickstyp) och vi arbetar med
  // huvudutskicket.
  const showTypeStep = led === 0 && products.length > 1;
  const isStepComplete = (() => {
    if (led !== 0) return [stepReminderDateComplete, stepDeliveryComplete, stepTextsComplete, stepSummaryComplete];
    const steps = [stepMembersComplete, stepDeliveryComplete, stepTextsComplete, stepSummaryComplete];
    return showTypeStep ? [stepTypeComplete, ...steps] : steps;
  })();
  const allStepsComplete = isStepComplete.every((v) => v);

  // Förklaring till varför Fortsätt är låst i respektive steg (visas som tooltip på knappen).
  const stepBlockedReasons = (() => {
    if (led !== 0) {
      return [
        'Välj utskicksdatum och förfallodatum för påminnelsen.',
        'Välj minst en leveransmetod för påminnelsen.',
        'Texterna måste vara klarmarkerade.',
        '',
      ];
    }

    const reasons = [
      'Välj hur medlemmarna ska väljas ut och klarmarkera alla korten (förfallodatum, inkludera och exkludera).',
      'Slutför leveransvalet så att alla mottagare kan nås (lägg till fler metoder eller använd samtliga nivåer).',
      'Alla flikar måste vara klarmarkerade: texterna och fakturablanketten.',
      '',
    ];

    return showTypeStep ? ['Välj vad du vill beställa för att gå vidare.', ...reasons] : reasons;
  })();

  // Utskickstyp härleds ur produktnamnet (typ-fält saknas): innehåller "påminnel" => påminnelseavisering.
  const selectedProductName = products.find((p) => p.id === watch('product_id'))?.name ?? '';
  const isReminderType = selectedProductName.toLowerCase().includes('påminnel');
  // Påminnelser kan bara beställas på beställningar som aviserar – utan fakturor finns inget att påminna om.
  const isInvoicingType = selectedProductName.toLowerCase().includes('medlemsavis');

  // En inskickad/levererad beställning är låst för redigering – FS hanterar den. Bara utkast och "Komplettering
  // behövs" går att ändra (det senare för att fylla i texterna). Låst läge = skrivskyddad vy, ingen sparning.
  const orderStatus = watch('status') as number | undefined;
  // Huvudutskicket är inskickat och därmed avslutat för kundens del.
  const mainSubmitted = orderStatus === SendingOrderStatusEnum.Inskickad || orderStatus === SendingOrderStatusEnum.Levererad;
  // Den aktiva påminnelsen är redan beställd?
  const ledStatus = (() => {
    if (led === 1) return sendingOrder?.reminder1_status ?? SendingOrderStatusEnum.Pagaende;
    if (led === 2) return sendingOrder?.reminder2_status ?? SendingOrderStatusEnum.Pagaende;
    return orderStatus;
  })();
  const ledOrdered = (() => {
    if (led === 1) return !!sendingOrder?.reminder1_date_id;
    if (led === 2) return !!sendingOrder?.reminder2_date_id;
    return false;
  })();
  // Låst = skrivskyddat läge. Gäller PER LED: ett inskickat huvudutskick låser inte påminnelserna, det är hela
  // poängen med att kunna öppna en inskickad beställning och boka dem i efterhand. En påminnelse som väntar på
  // komplettering är bokad men fortfarande redigerbar – precis som huvudutskicket i samma läge.
  const ledAwaitingCompletion = ledOrdered && ledStatus === SendingOrderStatusEnum.KompletteringBehovs;
  const locked = led === 0 ? mainSubmitted : ledOrdered && !ledAwaitingCompletion;
  // Ledläget byter innebörd på de två första stegen – etiketterna följer med.
  const showLedPicker = !!sendingOrder && orderStatus !== SendingOrderStatusEnum.Pagaende && isInvoicingType;
  // Landningsvyn: beställningen är inskickad och inget påminnelseled är valt. Då visas huvudutskicket som en
  // hopfälld rad och frågan om påminnelser – stegen hör till ett valt utskick och döljs här.
  const landing = showLedPicker && led === 0 && !mainStepsOpen;
  const activeStepLabels = (() => {
    if (led !== 0) return ledStepLabels;
    return showTypeStep ? stepLabels : stepLabelsWithoutType;
  })();

  // Bocken i steppern: Sammanfattning-steget (sista) bockas av först när beställningen är inskickad/levererad,
  // inte bara för att steget är giltigt. Övriga steg använder sin vanliga klar-status. (Submit-knappen använder allStepsComplete.)
  const stepIconComplete = isStepComplete.map((v, i) => (i === isStepComplete.length - 1 ? locked : v));

  // Utskicksdatum-panelen: betaldatum ur formuläret + härlett medlemsår/friperiod ur datum + org-inställningar.
  const payDateStr = watch('payDate') as string | undefined;
  const panelPayDate = payDateStr ? dayjs(payDateStr) : null;
  const membershipResult = deriveMembershipYear({ sendDate, payDate: panelPayDate, expireMMdd: membershipMMdd, graceDays: gracePeriodDays });

  // Serialiserar guidens arbetskopia (urval ur contexten + texter ur formuläret) till JSON för snd_order.wizardData.
  const buildWizardData = useCallback(
    (): string =>
      JSON.stringify({
        selectionTab,
        dueDateFrom: dueDateFrom ? dueDateFrom.format('YYYY-MM-DD') : null,
        dueDateTom: dueDateTom ? dueDateTom.format('YYYY-MM-DD') : null,
        profileFilters,
        excludeProfileFilters,
        dueConfirmed,
        includeConfirmed,
        excludeConfirmed,
        emailTextConfirmed,
        invoiceTextConfirmed,
        blankettConfirmed,
        coverage,
        completeLater: !!getValues('completeLater'),
        householdInvoice: getValues('householdInvoice') !== false,
        printZeroInvoice: getValues('printZeroInvoice') === true,
        useLocalAssLogo: getValues('useLocalAssLogo') === true,
        formTemplate: (getValues('formTemplate') as string) || '',
        textsFromPrevious: !!getValues('textsFromPrevious'),
        textsWriteChosen: !!getValues('textsWriteChosen'),
        letterColor: (getValues('letterColor') as string) ?? 'bw',
        emailHeading: getValues('emailHeading') ?? '',
        emailText: getValues('emailText') ?? '',
        invoiceHeading: getValues('invoiceHeading') ?? '',
        invoiceText: getValues('invoiceText') ?? '',
      } as WizardDataDraft),
    [
      selectionTab,
      dueDateFrom,
      dueDateTom,
      profileFilters,
      excludeProfileFilters,
      dueConfirmed,
      includeConfirmed,
      excludeConfirmed,
      emailTextConfirmed,
      invoiceTextConfirmed,
      blankettConfirmed,
      coverage,
      getValues,
    ],
  );

  // Formulärets nuvarande värden som en påminnelses arbetskopia. Ledet delar fält med huvudutskicket – vad
  // fälten betyder avgörs av vilket led som är aktivt.
  const currentReminderDraft = useCallback(
    (): ReminderDraft => ({
      date_id: (getValues('date_id') as number) || undefined,
      payDate: (getValues('payDate') as string) || undefined,
      deliveryMethodOrder: (getValues('deliveryMethodOrder') as string) || '',
      emailHeading: (getValues('emailHeading') as string) || '',
      emailText: (getValues('emailText') as string) || '',
      invoiceHeading: (getValues('invoiceHeading') as string) || '',
      invoiceText: (getValues('invoiceText') as string) || '',
      emailTextConfirmed,
      invoiceTextConfirmed,
      textsFromPrevious: !!getValues('textsFromPrevious'),
      textsWriteChosen: !!getValues('textsWriteChosen'),
    }),
    [getValues, emailTextConfirmed, invoiceTextConfirmed],
  );

  // Har ledet en påbörjad (ej inskickad) beställning?
  const hasReminderDraft = useCallback(
    (which: 1 | 2): boolean => {
      const draft = reminderDrafts[String(which)];
      return !!draft && (!!draft.date_id || !!draft.invoiceText || !!draft.emailText);
    },
    [reminderDrafts],
  );

  // Laddar formuläret med ett annat leds värden. Huvudutskicket läses från beställningen + arbetskopian,
  // påminnelserna från sina kolumner (beställda) eller sitt utkast.
  const loadLedIntoForm = useCallback(
    (target: number, drafts: Record<string, ReminderDraft>) => {
      if (!sendingOrder) return;

      if (target === 0) {
        let w: WizardDataDraft = {};
        try {
          w = sendingOrder.wizardData ? (JSON.parse(sendingOrder.wizardData) as WizardDataDraft) : {};
        } catch (e) {
          ConsoleHelper.log('error parse wizardData', e);
        }
        setValue('date_id', sendingOrder.date_id);
        setValue('payDate', sendingOrder.payDate);
        setValue('deliveryMethodOrder', sendingOrder.deliveryMethodOrder ?? '');
        setValue('emailHeading', w.emailHeading ?? '');
        setValue('emailText', w.emailText ?? '');
        setValue('invoiceHeading', w.invoiceHeading ?? '');
        setValue('invoiceText', w.invoiceText ?? '');
        setValue('completeLater', w.completeLater ?? false);
        setValue('textsFromPrevious', w.textsFromPrevious ?? false);
        setValue('textsWriteChosen', w.textsWriteChosen ?? false);
        setEmailTextConfirmed(!!w.emailTextConfirmed);
        setInvoiceTextConfirmed(!!w.invoiceTextConfirmed);
        setBlankettConfirmed(!!w.blankettConfirmed);
        setSendDate(sendingOrder.date?.sndDate ? dayjs(sendingOrder.date.sndDate) : null);
        return;
      }

      const ordered = target === 1 ? !!sendingOrder.reminder1_date_id : !!sendingOrder.reminder2_date_id;
      const draft = drafts[String(target)] ?? {};

      // Beställd påminnelse läses från sina kolumner, en påbörjad från arbetskopian.
      const orderedDateId = target === 1 ? sendingOrder.reminder1_date_id : sendingOrder.reminder2_date_id;
      const orderedPayDate = target === 1 ? sendingOrder.reminder1_payDate : sendingOrder.reminder2_payDate;
      const orderedMethods = target === 1 ? sendingOrder.reminder1_deliveryMethodOrder : sendingOrder.reminder2_deliveryMethodOrder;
      const orderedSndDate = target === 1 ? sendingOrder.reminder1_date?.sndDate : sendingOrder.reminder2_date?.sndDate;

      const dateId = ordered ? orderedDateId : draft.date_id;
      const payDate = ordered ? orderedPayDate : draft.payDate;
      const methods = ordered ? orderedMethods : draft.deliveryMethodOrder;
      const ledDate = ordered ? orderedSndDate : undefined;

      setValue('date_id', dateId as number);
      setValue('payDate', payDate);
      setValue('deliveryMethodOrder', methods ?? '');
      setValue('emailHeading', draft.emailHeading ?? '');
      setValue('emailText', draft.emailText ?? '');
      setValue('invoiceHeading', draft.invoiceHeading ?? '');
      setValue('invoiceText', draft.invoiceText ?? '');

      // En färdig påminnelse öppnas som klar; en som väntar på komplettering eller är påbörjad behåller sitt läge.
      const ledStatusForTarget = target === 1 ? sendingOrder.reminder1_status : sendingOrder.reminder2_status;
      const awaitingCompletion = ordered && ledStatusForTarget === SendingOrderStatusEnum.KompletteringBehovs;
      const done = ordered && !awaitingCompletion;

      // "Kompletteras senare" speglar påminnelsens faktiska läge, inte huvudutskickets.
      setValue('completeLater', awaitingCompletion);
      // Lägesknapparna i Texter: en ny eller påbörjad påminnelse får samma val som huvudutskicket – hämta texter
      // från en tidigare avisering, eller skriv själv.
      setValue('textsFromPrevious', done || !!draft.textsFromPrevious);
      setValue('textsWriteChosen', done || !!draft.textsWriteChosen);
      setEmailTextConfirmed(done || !!draft.emailTextConfirmed);
      setInvoiceTextConfirmed(done || !!draft.invoiceTextConfirmed);

      if (ledDate) {
        setSendDate(dayjs(ledDate));
        return;
      }

      // Ett påbörjat utkast bär bara date_id – slå upp datumet så tidslinjen och headern visar det valda utskicket
      // (annars ser det ut som att inget datum är valt trots att kortet är grönt).
      setSendDate(null);

      if (dateId) {
        (async () => {
          try {
            const res = await getSendingDateById(dateId);
            if (isMounted() && res.data?.sndDate) setSendDate(dayjs(res.data.sndDate));
          } catch (e) {
            ConsoleHelper.log('error getSendingDateById', e);
          }
        })();
      }
    },
    [sendingOrder, setValue, setSendDate, setEmailTextConfirmed, setInvoiceTextConfirmed, setBlankettConfirmed, isMounted],
  );

  // Läser om beställningen efter att en påminnelse beställts. Rör bara orderdatan, inte formuläret.
  const refreshOrder = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getSendingOrderById(id);
      if (isMounted()) setSendingOrder(res.data);
    } catch (e) {
      ConsoleHelper.log('error refreshOrder', e);
    }
  }, [id, isMounted]);

  // Sparar den aktiva påminnelsens arbetskopia i beställningens wizardData. Huvudutskickets fält tas från den
  // SPARADE beställningen – formuläret bär ju påminnelsens värden när ett påminnelseled är aktivt.
  const saveReminderDraft = useCallback(async () => {
    if (!id || !sendingOrder || led === 0) return;

    const drafts = { ...reminderDrafts, [String(led)]: currentReminderDraft() };

    let stored: WizardDataDraft = {};
    try {
      stored = sendingOrder.wizardData ? (JSON.parse(sendingOrder.wizardData) as WizardDataDraft) : {};
    } catch (e) {
      ConsoleHelper.log('error parse wizardData', e);
    }

    const payload: SendingOrder = {
      ...sendingOrder,
      id: Number(id),
      wizardData: JSON.stringify({ ...stored, reminders: drafts }),
      updated: new Date(),
    };

    setReminderDrafts(drafts);

    try {
      await updateSendingOrder(payload);
      if (isMounted()) setSendingOrder(payload);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log('error saveReminderDraft', e);
    }
  }, [id, sendingOrder, led, reminderDrafts, currentReminderDraft, isMounted]);

  // Tyst utkast-sparning (ingen toast/navigering). Anropas vid varje stegbyte så beställningen alltid går att återuppta.
  const saveDraft = useCallback(async () => {
    if (!id) return;

    // Påminnelseleden sparas i arbetskopian; ett låst huvudutskick sparas inte alls.
    if (led !== 0) {
      if (!locked) await saveReminderDraft();
      return;
    }

    if (locked) return;
    const merged = { ...(sendingOrder ?? {}), ...getValues() } as SendingOrder;
    const payload: SendingOrder = {
      ...merged,
      id: Number(id),
      status: merged.status ?? SendingOrderStatusEnum.Pagaende,
      wizardData: buildWizardData(),
      updated: new Date(),
    };
    try {
      await updateSendingOrder(payload);
      if (isMounted()) setSendingOrder(payload);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log('error saveDraft', e);
    }
  }, [id, locked, led, saveReminderDraft, sendingOrder, getValues, buildWizardData, isMounted]);

  // Byter utskicksled: sparar det man lämnar och laddar det man går till.
  const switchLed = useCallback(
    async (target: number) => {
      if (target === led) return;

      const drafts = led === 0 ? reminderDrafts : { ...reminderDrafts, [String(led)]: currentReminderDraft() };

      if (led === 0) await saveDraft();
      else await saveReminderDraft();

      setLed(target);
      loadLedIntoForm(target, drafts);
    },
    [led, reminderDrafts, currentReminderDraft, saveDraft, saveReminderDraft, setLed, loadLedIntoForm],
  );

  // Skickar in den aktiva påminnelsen: mallens påminnelserad fylls i, uppdraget bokas i supportmodulen och FS notifieras.
  const submitReminder = useCallback(async () => {
    if (!id || led === 0) return;

    const draft = currentReminderDraft();

    if (!draft.date_id || !draft.payDate) {
      toast.error('Välj utskicksdatum och förfallodatum för påminnelsen.');
      return;
    }

    try {
      setSaving(true);
      const laterCompletion = !!getValues('completeLater');

      await orderSendingOrderReminder(Number(id), led as 1 | 2, {
        date_id: draft.date_id,
        payDate: dayjs(draft.payDate).format('YYYY-MM-DD'),
        deliveryMethodOrder: draft.deliveryMethodOrder ?? '',
        invoiceHeading: draft.invoiceHeading ?? '',
        invoiceText: draft.invoiceText ?? '',
        emailHeading: draft.emailHeading ?? '',
        emailText: draft.emailText ?? '',
        completeLater: laterCompletion,
      });
      if (!isMounted()) return;
      toast.success(laterCompletion ? `Påminnelse ${led} är bokad. Fyll i texterna när du är redo.` : `Påminnelse ${led} är beställd.`);
      await refreshOrder();
    } catch (e) {
      ConsoleHelper.log('error orderSendingOrderReminder', e);
      toast.error('Påminnelsen kunde inte beställas.');
    } finally {
      if (isMounted()) setSaving(false);
    }
  }, [id, led, currentReminderDraft, getValues, isMounted, refreshOrder]);

  // Skapar fakturamallen (urval + texter) och returnerar dess id, eller null om något går fel.
  // Anropas bara vid faktisk inskickning, så texterna är alltid färdiga (textApproved=true, notReady=false).
  const createTemplate = useCallback(
    async (order: SendingOrder): Promise<number | null> => {
      const wantsEmail = ((order.deliveryMethodOrder as string) ?? '').split(',').some((m) => m === 'mail' || m === 'kivra');

      // Betalningsvillkor i ANTAL DAGAR (förfallodagar) = sista betaldatum − utskicksdatum. inv_templateSendings.expireDate
      // är ett dagantal (inte ett datum) eftersom mallen återanvänds för nya utskick – förfallodatumet räknas då ut vid
      // genereringen som genereringsdatum + expireDate dagar. Faller tillbaka på 30 dagar om datum saknas.
      const payDateObj = order.payDate ? dayjs(order.payDate) : null;
      const paymentDays = sendDate && payDateObj ? Math.max(0, payDateObj.diff(sendDate, 'day')) : 30;
      const sendingExpireDate = String(paymentDays);
      // Vem som skapade/redigerade texterna – beställaren (faller tillbaka på "Administratör").
      const lastEditedBy = order.createdByName || 'Administratör';

      const sendings: Sending[] = [];
      if (wantsEmail) {
        sendings.push({
          sendingType: InvoiceSendingTypeIntegerEnum.Email,
          isReminder: false,
          // method = leveransmetod (mall-default). "mail" = e-post – giltigt värde (letter/mail/file/print/home/card/member);
          // tidigare "email"/"main" var ogiltiga och kastar vid generering. E-post är standard i skapade mallar.
          method: 'mail',
          status: 0,
          defaultHeading: order.emailHeading ?? '',
          defaultText: order.emailText ?? '',
          emailHeading: order.emailHeading ?? '',
          emailText: order.emailText ?? '',
          customerEditEnabled: true,
          textApproved: true,
          notReady: false,
          expireDate: sendingExpireDate,
          reminderDate: '',
          isEmailHtml: true,
          smsBody: '',
          lastEditedBy,
        } as Sending);
      }
      sendings.push({
        sendingType: InvoiceSendingTypeIntegerEnum.Main,
        isReminder: false,
        // Mall-defaultens leveransmetod = e-post ("mail"). Kunden/supporten kan ändra i 1.0. (Det faktiska utskickets
        // kanaler styrs av snd_order.deliveryMethodOrder, inte av detta fält.)
        method: 'mail',
        status: 0,
        defaultHeading: order.invoiceHeading ?? '',
        defaultText: order.invoiceText ?? '',
        // E-post/Kivra-texten läggs även på Main-sendingen: mall-redigeringen visar E-post & Kivra-blocket utifrån
        // Main-sendingens emailHeading/emailText (Email-sendingen ovan används av genereringen för e-postutskicket).
        emailHeading: order.emailHeading ?? '',
        emailText: order.emailText ?? '',
        customerEditEnabled: true,
        textApproved: true,
        notReady: false,
        expireDate: sendingExpireDate,
        reminderDate: '',
        isEmailHtml: true,
        smsBody: '',
        lastEditedBy,
        // Artikelnummer/-prefix för fakturans brev-utskick, härlett ur utskickstyp + färgval. Sätts bara på Main-sendingen.
        articleGroup: brevArticlePrefix(isReminderType, (order.letterColor ?? 'bw') === 'color'),
      } as Sending);

      // Påminnelse 1 & 2 måste finnas i mallen för att alla fyra flikar ska gå att redigera i 1.0. Wizarden samlar inte
      // in påminnelsetexter, så de skapas tomma och notReady (visas som "Ej klar"-flikar) med rimliga default-offsets –
      // supporten fyller i texter och justerar dagar i 1.0. reminderDate = utskicksfördröjning (dagar efter föregående
      // sendings förfallodag); expireDate = påminnelsens egna förfallodagar.
      const reminderSending = (type: number): Sending =>
        ({
          sendingType: type,
          isReminder: true,
          method: 'mail',
          status: 0,
          defaultHeading: '',
          defaultText: '',
          emailHeading: '',
          emailText: '',
          customerEditEnabled: true,
          textApproved: false,
          notReady: true,
          expireDate: sendingExpireDate,
          reminderDate: '14',
          isEmailHtml: true,
          smsBody: '',
          lastEditedBy,
        } as Sending);
      sendings.push(reminderSending(InvoiceSendingTypeIntegerEnum.Reminder1));
      sendings.push(reminderSending(InvoiceSendingTypeIntegerEnum.Reminder2));

      // Auto-genererat mallnamn som skiljer mallarna åt: utskickstyp + medlemsår + kort urval. Utskicksdatum utelämnas
      // (skapatdatum adderas ändå vid avisering); beställningsnr (#id) sist för spårbarhet/unikhet.
      const membershipForName = deriveMembershipYear({ sendDate, payDate: payDateObj, expireMMdd: membershipMMdd, graceDays: gracePeriodDays });
      const medlemsarPart = membershipForName ? ` medlemsår ${membershipForName.expiryYear}` : '';
      const includeNames = profileFilters.map((f) => f.itemName).filter((n) => !!n);
      let urvalPart: string;
      if (includeNames.length) urvalPart = includeNames.join(', ');
      else if (selectionTab === 1 && order.selectionDescription) urvalPart = order.selectionDescription;
      else urvalPart = 'alla medlemmar';
      if (urvalPart.length > 40) urvalPart = `${urvalPart.slice(0, 39).trimEnd()}…`;

      const template: Partial<InvoiceTemplate> = {
        owner_id: order.owner_id ?? 0,
        name: `${isReminderType ? 'Påminnelseavisering' : 'Medlemsavisering'}${medlemsarPart} – ${urvalPart} (#${order.id ?? ''})`.trim(),
        type: 'member',
        // Fakturablankett enligt kundens val i Texter-steget (begränsas av faktureringsgruppens kontotyp). Faller
        // tillbaka på bankgiro-OCR om inget val hunnit sättas.
        formTemplate: order.formTemplate || 'bgocr_2024',
        // Krävs för att mallen ska listas under Fakturamallar (/settings/invoicegroup/invoice-templates filtrerar på showFormInfo=true).
        showFormInfo: true,
        expireDateFrom: dueDateFrom ? dueDateFrom.format('YYYYMMDD') : '',
        expireDateTom: dueDateTom ? dueDateTom.format('YYYYMMDD') : '',
        profilesInclude: profilePairs(profileFilters),
        profilesExclude: profilePairs(excludeProfileFilters),
        // Spegla en manuellt uppsatt malls kolumnvärden (0 / tom sträng) i stället för att lämna dem NULL,
        // så DB-raden ser likadan ut som FS egna mallar och slipper NULL-relaterade fel i efterföljande flöden.
        selection: '',
        // Skapa faktura även för avgiftsfria medlemmar enligt kundens val i Medlemmar-steget (Avancerat). Default nej.
        printZeroInvoice: order.printZeroInvoice === true,
        // Samlingsfaktura enligt kundens val i Medlemmar-steget (default Ja).
        householdInvoice: order.householdInvoice !== false,
        newspaper: 0,
        memberType: 0,
        email: '',
        status: '0',
        cardInfo: '',
        // Logotyp enligt kundens val i Texter-steget: 1 = föreningens egna logga, 0 = förbundets (centrala) logga.
        useLocalAssLogo: order.useLocalAssLogo === true ? 1 : 0,
        useLocalAssAddress: 0,
        sendings,
      };

      try {
        const res = await createInvoiceTemplate(template);
        return typeof res.data === 'number' ? res.data : null;
      } catch (e) {
        ConsoleHelper.log('error createInvoiceTemplate', e);
        return null;
      }
    },
    [dueDateFrom, dueDateTom, profileFilters, excludeProfileFilters, isReminderType, sendDate],
  );

  const persist = useCallback(
    async (status: number) => {
      const merged = { ...(sendingOrder ?? {}), ...getValues() } as SendingOrder;
      const payload: SendingOrder = {
        ...merged,
        id: id ? Number(id) : merged.id,
        status,
        wizardData: buildWizardData(),
        updated: new Date(),
      };

      const isFinal = status === SendingOrderStatusEnum.Inskickad || status === SendingOrderStatusEnum.KompletteringBehovs;

      try {
        setSaving(true);

        // Fakturamallen (urval + texter) skapas bara vid faktisk inskickning – inte vid "kompletteras senare",
        // då texterna ännu inte finns. En komplettering-beställning får sin mall först när den slutförs (blir Inskickad),
        // med de då ifyllda texterna. Skapas bara en gång (undviker dubbletter).
        if (status === SendingOrderStatusEnum.Inskickad && !payload.template_id) {
          const templateId = await createTemplate(payload);
          if (templateId) payload.template_id = templateId;
        }

        await updateSendingOrder(payload);
        if (!isMounted()) return;
        if (isFinal) {
          // Kvittodialog i stället för en flyktig toast. Bekräftelsen går till beställarens ANGIVNA e-post (createdByEmail
          // från steg 1), inte den inloggade användarens – så vi visar samma adress som mailet faktiskt skickas till.
          const bestallareEmail = (getValues('createdByEmail') as string) || currentUser?.email || '';
          setSubmittedInfo({ email: bestallareEmail, status });
        } else {
          toast.success('Beställningen har sparats.');
          setSendingOrder(payload);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error updateSendingOrder', e);
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [sendingOrder, getValues, id, isMounted, currentUser, createTemplate, buildWizardData],
  );

  const { step, steps, currentStepIndex, isFirstStep, isLastStep, back, next, goTo } = useMultiStepForm([
    // Påminnelser börjar med sitt eget datumsteg (med tidslinje mot huvudutskicket). Huvudutskicket börjar med
    // typ-steget – men bara när det finns fler än en utskickstyp att välja mellan.
    ...(led !== 0 && sendingOrder ? [<SendingWizardStepReminderDate order={sendingOrder} which={led as 1 | 2} readOnly={locked} />] : []),
    ...(showTypeStep ? [<SendingWizardStep2Type />] : []),
    // Medlemsurvalet hör bara till huvudutskicket – en påminnelse ärver det och hoppar över steget.
    ...(led === 0 ? [<SendingWizardStep5Selection />] : []),
    <SendingWizardStep4Delivery />,
    <SendingWizardStep6Texts />,
    <SendingWizardStep7Summary />,
  ]);

  // Skrivskyddet gäller stegens innehåll. Landningsvyn (där påminnelser väljs) ligger utanför stegen och har
  // därför alltid egen interaktion – undantaget som tidigare behövdes här är inte längre aktuellt.
  const contentLocked = locked;

  // Nollställ vy-läget när en beställning öppnas. Guiden kan ligga kvar monterad mellan öppningar (modal över
  // listan), och då skulle valt utskicksled och öppnade steg följa med in i nästa beställning – man hamnade i
  // steppern i stället för i översikten.
  useEffect(() => {
    setLed(0);
    setMainStepsOpen(false);
    setKivraActivated(null);
    jumpedToPendingRef.current = null;
  }, [id, setLed]);

  // Antal obetalda säger något först när föregående utskicks förfallodatum passerat – dessförinnan döljs siffrorna.
  useEffect(() => {
    if (led === 0 || !sendingOrder) {
      setReminderCountsVisible(false);
      setReminderPreviousPayDate(null);
      return;
    }

    const previousPayDate = led === 2 ? sendingOrder.reminder1_payDate : sendingOrder.payDate;

    setReminderPreviousPayDate(previousPayDate ? dayjs(previousPayDate) : null);
    setReminderCountsVisible(!!previousPayDate && dayjs().startOf('day').isAfter(dayjs(previousPayDate), 'day'));
  }, [led, sendingOrder, setReminderCountsVisible, setReminderPreviousPayDate]);

  // Kivra-aktivering på faktureringsgruppen (kivraTenantKey). Samma auktoritativa signal som leveranssteget använder.
  useEffect(() => {
    const ownerId = sendingOrder?.owner_id;

    if (!ownerId || ownerId <= 0) return;

    (async () => {
      try {
        const res = await getInvoiceGroupById(ownerId);
        if (isMounted()) setKivraActivated(!!(res.data?.kivraTenantKey ?? '').trim());
      } catch (e) {
        if (isMounted()) setKivraActivated(null);
      }
    })();
  }, [sendingOrder?.owner_id, isMounted]);

  // Ett utskick som väntar på komplettering öppnas på det steg som faktiskt saknar något: leveransmetoderna när
  // Kivra är valt men inte aktiverat, annars texterna. Gäller både huvudutskicket och en påminnelse.
  useEffect(() => {
    if (!sendingOrder) return;
    if (jumpedToPendingRef.current === led) return;

    const awaitingCompletion = led === 0 ? orderStatus === SendingOrderStatusEnum.KompletteringBehovs : ledAwaitingCompletion;

    if (!awaitingCompletion) return;
    // Huvudutskicket hoppar bara när stegen faktiskt är öppnade (annars visas landningsvyn).
    if (led === 0 && !mainStepsOpen) return;
    // Vänta tills Kivra-kollen är klar, annars kan vi hoppa till fel steg.
    if (kivraChosen && kivraActivated === null) return;

    jumpedToPendingRef.current = led;

    const textsIndex = activeStepLabels.length - 2;
    const deliveryIndex = activeStepLabels.length - 3;

    goTo(kivraMissing ? deliveryIndex : textsIndex);
    scrollToTop();
  }, [
    sendingOrder,
    led,
    ledAwaitingCompletion,
    mainStepsOpen,
    orderStatus,
    kivraChosen,
    kivraActivated,
    kivraMissing,
    activeStepLabels,
    goTo,
    scrollToTop,
  ]);

  // Byte av utskicksled börjar alltid om från ledets första steg.
  const previousLedRef = useRef(led);
  useEffect(() => {
    if (previousLedRef.current === led) return;
    previousLedRef.current = led;
    goTo(0);
    scrollToTop();
  }, [led, goTo, scrollToTop]);

  // Vid varje stegbyte sparas utkastet tyst så inget går förlorat. En spinner visas kort, sedan fadar nästa steg in.
  const runTransition = useCallback(
    (action: () => void) => {
      saveDraft();
      setTransitioning(true);
      window.setTimeout(() => {
        if (!isMounted()) return;
        action();
        scrollToTop();
        setTransitioning(false);
      }, 300);
    },
    [saveDraft, scrollToTop, isMounted],
  );
  // Texter-steget har interna flikar; dess index beror på vilka steg som faktiskt visas (typ-steg och medlemssteg
  // finns bara i vissa lägen). Texter ligger alltid näst sist.
  const textsStepIndex = activeStepLabels.length - 2;
  // Texter-steget har interna flikar. Där bläddrar Fortsätt/Tillbaka mellan flikarna först och går till
  // nästa/föregående steg först när man står på sista respektive första fliken (registreras via textStepNav).
  const handleNext = useCallback(() => {
    if (currentStepIndex === textsStepIndex && textStepNav?.hasTabs && !textStepNav.atLastTab) {
      textStepNav.goNextTab();
      return;
    }
    runTransition(next);
  }, [currentStepIndex, textsStepIndex, textStepNav, runTransition, next]);
  const handleBack = useCallback(() => {
    if (currentStepIndex === textsStepIndex && textStepNav?.hasTabs && !textStepNav.atFirstTab) {
      textStepNav.goPrevTab();
      return;
    }
    runTransition(back);
  }, [currentStepIndex, textsStepIndex, textStepNav, runTransition, back]);
  const handleGoTo = useCallback((i: number) => runTransition(() => goTo(i)), [runTransition, goTo]);
  // Steg-indikatorerna i headern följer samma spärr som Fortsätt-knappen: ett steg är klickbart bara om alla
  // föregående steg är klara. En låst (inskickad/levererad) beställning tillåter fri navigering (läsläge).
  const canNavigateToStep = (i: number) => locked || isStepComplete.slice(0, i).every(Boolean);
  // Avboka = radera beställningen. Bekräftas i dialog; efteråt tillbaka till listan.
  const handleCancelClose = useCallback(
    async (value?: { id: string }) => {
      setCancelOpen(false);

      if (!value || !id) return;

      try {
        setSaving(true);
        await deleteSendingOrder(Number(id));
        if (!isMounted()) return;
        toast.success('Beställningen är avbokad.');
        navigate(RoutesEnum.Sending);
      } catch (e) {
        ConsoleHelper.log('error deleteSendingOrder', e);
        toast.error('Beställningen kunde inte avbokas.');
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [id, isMounted, navigate],
  );

  // Stäng fullskärmsläget → tillbaka till beställningslistan.
  const handleClose = useCallback(() => navigate(RoutesEnum.Sending), [navigate]);

  // Fullskärm på laptops/mindre skärmar (< xl) så hela höjden kan användas; på stora skärmar en stor centrerad modal.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xl'));

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='lg'
      fullWidth
      fullScreen={fullScreen}
      // TinyMCE:s dialoger (t.ex. Källkod) renderas i en portal utanför denna modal. Utan detta drar modalens
      // fokus-lås tillbaka fokus, så man inte kan markera/redigera i TinyMCE-dialogen.
      disableEnforceFocus
      sx={{
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          height: fullScreen ? '100%' : '90vh',
          maxHeight: fullScreen ? '100%' : '90vh',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* Topbar: rubrik + status + datum/medlemsår + Ändra + stäng, centrerad i samma bredd som innehållet. */}
      <Box sx={{ flexShrink: 0, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          {/* Vänster: rubrik = utskickstyp + utskicksdatum med statusbricka. */}
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {led === 0
                ? `${selectedProductName || 'Medlemsavisering'}${sendDate ? ` ${formatCustomerSendDate(sendDate)}` : ''}`
                : `Påminnelse ${led}${sendDate ? ` ${formatCustomerSendDate(sendDate)}` : ''}`}
            </Typography>
            <Box sx={{ ml: 1 }}>
              <SendingOrderStatusBadge status={led === 0 ? orderStatus : ledStatus} size='small' />
            </Box>
          </Box>

          {/* Höger: utskicksdatum / sista betaldatum / medlemsår + Ändra + stäng. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
            {(landing
              ? []
              : [
                  { label: 'Utskicksdatum', value: sendDate ? formatCustomerSendDate(sendDate) : '—' },
                  { label: 'Sista betaldatum', value: panelPayDate ? panelPayDate.format('YYYY-MM-DD') : '—' },
                  { label: 'Medlemsår', value: membershipResult ? String(membershipResult.expiryYear) : '—' },
                ]
            ).map((it) => (
              <Box key={it.label}>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600, letterSpacing: 0.6, display: 'block', lineHeight: 1.4 }}>
                  {it.label}
                </Typography>
                <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  {it.value}
                </Typography>
              </Box>
            ))}
            {!locked && !landing && (
              <Button variant='outlined' size='small' startIcon={<EditCalendarOutlined />} onClick={() => setDateModalOpen(true)}>
                Ändra
              </Button>
            )}
            {/* Avboka = radera beställningen. Finns oavsett status – en bokad beställning behöver också kunna
                avbokas – men bara för huvudutskicket; en enskild påminnelse avbokas av Föreningssupport. */}
            {led === 0 && (
              <Button variant='outlined' size='small' color='error' disabled={saving} onClick={() => setCancelOpen(true)}>
                {mainSubmitted || orderStatus === SendingOrderStatusEnum.KompletteringBehovs ? 'Avboka' : 'Radera'}
              </Button>
            )}
            <IconButton aria-label='Stäng' onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Scrollbar innehållsyta – centrerad kolumn, vit bakgrund (fokus på att fylla i stegen). */}
      <Box ref={bodyRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: 'background.paper', px: { xs: 2, sm: 3 }, py: 3 }}>
        <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          {landing && sendingOrder ? (
            <SendingOrderReminderLanding
              order={sendingOrder}
              canOrderReminders={mainSubmitted}
              hasDraft={hasReminderDraft}
              onSelect={(which) => switchLed(which)}
              onOpenMain={() => setMainStepsOpen(true)}
              summary={<SendingWizardStep7Summary />}
            />
          ) : (
            <>
              {/* Kompakt stepper på mobil: aktuellt steg + en segmenterad progress-rad (ikon-steppern spiller över på små skärmar). */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2, mt: 0.5 }}>
                <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
                  Steg {currentStepIndex + 1} av {steps.length}
                </Typography>
                <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1 }}>
                  {currentStepIndex + 1}. {activeStepLabels[currentStepIndex]}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {activeStepLabels.map((label, i) => {
                    const navigable = canNavigateToStep(i);
                    return (
                      <Box
                        key={label}
                        onClick={() => navigable && handleGoTo(i)}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          cursor: navigable ? 'pointer' : 'not-allowed',
                          opacity: navigable ? 1 : 0.45,
                          // eslint-disable-next-line no-nested-ternary
                          bgcolor: i === currentStepIndex ? 'primary.main' : stepIconComplete[i] ? 'success.main' : 'grey.300',
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Kompakt stegindikator (segment + etikett per steg) på större skärmar – som skapa-modalens men med text. */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, mb: 2, mt: 0.5 }}>
                {activeStepLabels.map((label, i) => {
                  const active = i === currentStepIndex;
                  const done = stepIconComplete[i];
                  const navigable = canNavigateToStep(i);
                  let barColor = 'grey.300';
                  if (active) barColor = 'primary.main';
                  else if (done) barColor = 'success.main';
                  return (
                    <Box
                      key={label}
                      onClick={() => navigable && handleGoTo(i)}
                      sx={{ flex: 1, minWidth: 0, cursor: navigable ? 'pointer' : 'not-allowed', opacity: navigable ? 1 : 0.5 }}
                    >
                      <Box sx={{ height: 5, borderRadius: 3, bgcolor: barColor, mb: 0.75, transition: 'background-color .2s' }} />
                      <Typography
                        variant='caption'
                        sx={{
                          display: 'block',
                          fontWeight: active ? 700 : 500,
                          color: active || done ? 'text.primary' : 'text.secondary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {i + 1}. {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {locked && (
                <Alert severity='info' sx={{ mb: 1 }}>
                  {(() => {
                    if (led !== 0) return `Påminnelse ${led} är beställd och kan inte längre redigeras.`;
                    const ord = orderStatus === SendingOrderStatusEnum.Levererad ? 'levererad' : 'inskickad';
                    if (showLedPicker) return `Huvudutskicket är ${ord} och kan inte längre redigeras. Du kan fortfarande beställa påminnelser.`;
                    return `Beställningen är ${ord} och kan inte längre redigeras.`;
                  })()}
                  <Box sx={{ mt: 0.5 }}>
                    Vill du ändra texterna gör du det i fakturamallen: <strong>Inställningar &rarr; Fakturering &rarr; Fakturamallar</strong>.
                  </Box>
                </Alert>
              )}

              {!locked && deadlinePassed && (
                <Alert severity='error' sx={{ mb: 1 }}>
                  Sista dagen att skicka in det här utskicket var <strong>{deadlineDisplay}</strong> – produktionen behöver en vecka före
                  utskicksdatumet. Välj ett senare utskicksdatum för att kunna skicka in, eller kontakta Föreningssupport.
                </Alert>
              )}

              {/* Stegets innehåll centreras vertikalt (full bredd horisontellt); spinner under byte, annars fadar steget in. */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: '18rem',
                  opacity: contentLocked ? 0.7 : 1,
                  pointerEvents: contentLocked ? 'none' : 'auto',
                }}
              >
                {transitioning ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Fade in key={currentStepIndex} timeout={400}>
                    <Box sx={{ width: '100%' }}>{step}</Box>
                  </Fade>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Footer: navigering i linje med innehållskolumnen (inte längst ut i hörnet). Döljs på landningssidan,
          där korten är det som ska klickas. */}
      <Box
        sx={{
          display: landing ? 'none' : 'block',
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {(() => {
            if (!isFirstStep) {
              return (
                <Button type='button' variant='outlined' size='large' onClick={handleBack} startIcon={<ArrowBackIos fontSize='small' />}>
                  Tillbaka
                </Button>
              );
            }
            // Första steget i ett påminnelseled: tillbaka betyder tillbaka till beställningen.
            if (led !== 0) {
              return (
                <Button type='button' variant='outlined' size='large' onClick={() => switchLed(0)} startIcon={<ArrowBackIos fontSize='small' />}>
                  Till beställningen
                </Button>
              );
            }

            // Huvudutskickets steg öppnade från landningsvyn – tillbaka dit.
            if (mainStepsOpen) {
              return (
                <Button
                  type='button'
                  variant='outlined'
                  size='large'
                  onClick={() => setMainStepsOpen(false)}
                  startIcon={<ArrowBackIos fontSize='small' />}
                >
                  Till beställningen
                </Button>
              );
            }
            return <Box />;
          })()}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* En låst (inskickad/levererad) beställning visar bara navigering – ingen sparning eller inskickning. */}
            {!locked && (
              <Button
                type='button'
                variant='text'
                size='large'
                disabled={saving}
                onClick={async () => {
                  if (led === 0) {
                    persist(SendingOrderStatusEnum.Pagaende);
                    return;
                  }
                  await saveReminderDraft();
                  toast.success('Påminnelsen har sparats som utkast.');
                }}
              >
                Spara utkast
              </Button>
            )}
            {!isLastStep &&
              (() => {
                // Man får inte gå vidare förrän steget är klart: Typ (val gjort), Medlemmar (läge valt + klarmarkerat),
                // Leverans (finaliserad kedja) och Texter (klarmarkerade/kompletteras senare). Låst läge = fri navigering.
                let blocked = !locked && !isStepComplete[currentStepIndex];
                let reason = blocked ? stepBlockedReasons[currentStepIndex] ?? '' : '';
                // I Texter-steget med flikar gatar Fortsätt den aktuella fliken (klarmarkera → gå vidare till nästa
                // flik), inte hela steget – man behöver inte klarmarkera alla flikar innan man kan bläddra.
                if (!locked && currentStepIndex === 3 && textStepNav?.hasTabs) {
                  blocked = !textStepNav.canForward;
                  reason = blocked ? 'Klarmarkera den här fliken för att gå vidare.' : '';
                }
                return (
                  // Tooltip på en <span>-wrapper eftersom en disabled knapp inte tar emot hover-events.
                  <Tooltip title={reason} disableHoverListener={!reason}>
                    <span>
                      <Button
                        type='button'
                        variant='contained'
                        size='large'
                        disableElevation
                        disabled={blocked}
                        onClick={handleNext}
                        endIcon={<ArrowForwardIos fontSize='small' />}
                      >
                        Fortsätt
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()}
            {isLastStep && !locked && (
              <Badge
                sx={{
                  '& .MuiBadge-badge': { borderRadius: '100%', height: '25px' },
                  '& .badgeicon': { fontSize: '0.8rem' },
                }}
                color={allStepsComplete && !deadlinePassed ? 'success' : 'warning'}
                badgeContent={!allStepsComplete ? <PriorityHigh className='badgeicon' /> : <Check className='badgeicon' />}
              >
                <Button
                  type='button'
                  disableElevation
                  size='large'
                  variant={allStepsComplete ? 'contained' : 'outlined'}
                  disabled={!allStepsComplete || saving || deadlinePassed}
                  endIcon={<Send fontSize='small' />}
                  onClick={() => {
                    if (led !== 0) {
                      submitReminder();
                      return;
                    }
                    persist(completeLater ? SendingOrderStatusEnum.KompletteringBehovs : SendingOrderStatusEnum.Inskickad);
                  }}
                >
                  {(() => {
                    if (led !== 0) return completeLater ? 'Boka – texter kompletteras senare' : `Beställ påminnelse ${led}`;
                    return completeLater ? 'Spara – kompletteras senare' : 'Skicka in beställning';
                  })()}
                </Button>
              </Badge>
            )}
          </Box>
        </Box>
      </Box>

      <DeleteConfirmationDialog
        open={cancelOpen}
        value={id ? String(id) : ''}
        title={mainSubmitted || orderStatus === SendingOrderStatusEnum.KompletteringBehovs ? 'Avboka beställningen' : 'Radera beställningen'}
        modaltext={
          mainSubmitted || orderStatus === SendingOrderStatusEnum.KompletteringBehovs
            ? 'Beställningen tas bort och utskicket genomförs inte. Har påminnelser beställts avbokas de också. Detta går inte att ångra – kontakta Föreningssupport om du är osäker.'
            : 'Är du säker på att du vill radera beställningen? Detta går inte att ångra.'
        }
        confirmButtonText={mainSubmitted || orderStatus === SendingOrderStatusEnum.KompletteringBehovs ? 'Avboka' : 'Radera'}
        confirmButtonColor='error'
        onClose={handleCancelClose}
      />

      <SendingOrderDateModal
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        initialDateId={watch('date_id') as number | undefined}
        initialSendDate={sendDate}
        initialPayDate={panelPayDate}
        onSave={(dateId, newSendDate, newPayDate) => {
          setValue('date_id', dateId, { shouldValidate: true });
          setValue('payDate', newPayDate.format('YYYY-MM-DD'), { shouldValidate: true });
          setSendDate(newSendDate);
          setDateModalOpen(false);
          saveDraft();
        }}
      />

      {/* Kvittodialog efter inskickad beställning: bekräftar att beställningen tagits emot och att ett
          bekräftelsemejl skickats till beställarens e-post. Navigerar till beställningslistan vid stängning. */}
      <Dialog
        open={submittedInfo != null}
        maxWidth='xs'
        fullWidth
        onClose={() => {
          setSubmittedInfo(null);
          navigate(RoutesEnum.Sending);
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MarkEmailReadOutlined color='success' />
          {submittedInfo?.status === SendingOrderStatusEnum.KompletteringBehovs ? 'Beställningen är sparad' : 'Beställningen är inskickad'}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            {submittedInfo?.status === SendingOrderStatusEnum.KompletteringBehovs
              ? 'Vi hör av oss om komplettering – du kan fortsätta fylla i texterna när som helst.'
              : 'Tack! Vi har tagit emot din beställning och lägger upp utskicket.'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <MarkEmailReadOutlined fontSize='small' color='action' />
            <Typography variant='body2'>
              En bekräftelse har skickats till <strong>{submittedInfo?.email || 'din e-postadress'}</strong>.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant='contained'
            disableElevation
            onClick={() => {
              setSubmittedInfo(null);
              navigate(RoutesEnum.Sending);
            }}
          >
            Till mina beställningar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const SendingOrderWizardPage = () => {
  const formMethods = useForm<SendingOrder>({ mode: 'all' });

  return (
    <FormProvider {...formMethods}>
      <FormSelectableStateProvider>
        <SendingWizardProvider>
          <SendingOrderWizardBody />
        </SendingWizardProvider>
      </FormSelectableStateProvider>
    </FormProvider>
  );
};

export default SendingOrderWizardPage;
